import type { ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';
import {
  type DataProvider, useTranslate, useNotify, useRefresh, useDataProvider, Button, Form, useGetList, required,
} from 'react-admin';
import { useState, useEffect, useMemo } from 'react';
import { useFormContext, type FieldValues } from 'react-hook-form';
import {
  CircularProgress, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Stack,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import moment from 'moment';
import {
  buildDocumentsByAssetId,
  type CertificationDocument,
  type DocumentAssignee,
  validateBulkAssignments,
  validateBulkDocumentsUpload,
} from '../../../utils/certificationDocuments';
import { BulkDocumentAssignmentStep } from './BulkDocumentUserAssignmentsModal';
import { CertifiedUsersDatesStep } from './CertifiedUsersDatesStep';
import { CertificationDocumentInput } from './inputs/CertificationDocumentInput';
import {
  type AssigneeChoice,
  splitRecipientSelection,
  resolveCertifiedAssignees,
  buildAssigneeChoices,
  getGroupIdsWithAppUsers,
  filterAssigneeChoices,
  getCertifiedAssetIdsForCategory,
  userCertificationTransformer,
} from '../../../utils/certifiedUsersAssignments';
import { CertifiedUsersSelectionInput } from './inputs/CertifiedUserSelectionInput';

type ModalStep = 'selectRecipients' | 'enterDates' | 'uploadDocuments' | 'assignDocuments';

export type CertifiedUsersCategory = {
  id?: string;
  name: string;
  validityPeriod: ExpiryTimeGranularity;
  isDocumentaryProofRequired: boolean;
};

type UserCertificationDates = {
  firstIssuedOn: string;
  lastReceivedOn: string;
  expiryDate: string;
};

export type CertifiedUsersFormValue = {
  recipientChoiceIds: string[];
  certificationsByAssetId: Record<string, UserCertificationDates>;
  document?: CertificationDocument;
  documents: CertificationDocument[];
  assignments: Record<string, string>;
};

type AddCertifiedUsersModalProps = {
  open: boolean;
  onClose: () => void;
  categories: CertifiedUsersCategory[];
  onComplete?: () => void;
  onCreateCategories?: () => Promise<CertifiedUsersCategory[]>;
  excludeExistingForCategoryId?: string;
  choices?: AssigneeChoice[];
  certifiedAssetIds?: Set<string>;
  isChoicesLoading?: boolean;
};

const createUserCertification = (
  dataProvider: DataProvider,
  assetId: string,
  categoryId: string,
  dates: UserCertificationDates,
  document?: CertificationDocument,
) => dataProvider.create('user-certifications', {
  data: userCertificationTransformer({
    certificationCategory: categoryId,
    firstIssuedOn: dates.firstIssuedOn,
    lastReceivedOn: dates.lastReceivedOn,
    expiryDate: dates.expiryDate,
    plannedRenewalDate: null,
    ...(document?.rawFile ? { document } : {}),
  }),
  meta: { assetId },
});

const CertifiedUsersNextButton = (props: {
  step: ModalStep;
  choices: AssigneeChoice[];
  recipients: DocumentAssignee[];
  isExclusionLoading: boolean;
  isBulkDocumentFlow: boolean;
  validityPeriod?: CertifiedUsersCategory['validityPeriod'];
  certifiedAssetIds?: Set<string> | null;
  onRecipientsResolved: (recipients: DocumentAssignee[]) => void;
  onNext: (step: ModalStep) => void;
  onSkip: () => void;
}) => {

  const translate = useTranslate();
  const notify = useNotify();
  const { getValues, setValue, trigger } = useFormContext();
  const [isResolving, setIsResolving] = useState(false);

  const handleClick = async () => {

    if (props.step === 'selectRecipients') {

      if (props.isExclusionLoading) {

        return;

      }

      const selectedIds = (getValues('recipientChoiceIds') ?? []) as string[];
      const { userIds, groupIds } = splitRecipientSelection(selectedIds, props.choices);

      if (userIds.length === 0 && groupIds.length === 0) {

        if (selectedIds.length > 0) {

          setValue('recipientChoiceIds', []);
          notify(translate('resources.certification-categories.text.invalid_recipient_selection'), {
            type: 'warning',
          });
          return;

        }

        props.onSkip();
        return;

      }
      setIsResolving(true);
      try {

        const resolved = await resolveCertifiedAssignees(userIds, groupIds, {
          excludeAssetIds: props.certifiedAssetIds ?? undefined,
        });

        if (resolved.length === 0) {

          notify(
            translate('resources.certification-categories.text.all_users_already_certified'),
            { type: 'warning' },
          );
          return;

        }

        const today = moment().format('YYYY-MM-DD');
        const expiry = props.validityPeriod
          ? moment().add(props.validityPeriod.value, props.validityPeriod.type).format('YYYY-MM-DD')
          : today;
        const defaultDates = {
          firstIssuedOn: today,
          lastReceivedOn: today,
          expiryDate: expiry,
        };

        setValue(
          'certificationsByAssetId',
          Object.fromEntries(resolved.map((r) => [r.id, { ...defaultDates }])),
        );

        props.onRecipientsResolved(resolved);
        props.onNext('enterDates');

      } finally {

        setIsResolving(false);

      }
      return;

    }

    if (props.step === 'enterDates') {

      const valid = await trigger(undefined, { shouldFocus: true });

      if (!valid) return;

      if (props.isBulkDocumentFlow) {

        props.onNext('uploadDocuments');

      }

      return;

    }

    if (props.step === 'uploadDocuments') {

      const valid = await trigger(['documents'], { shouldFocus: true });

      if (!valid) return;

      setValue('assignments', {});
      props.onNext('assignDocuments');

    }

  };

  return (
    <Button
      label="general.text.next"
      onClick={handleClick}
      disabled={isResolving || props.isExclusionLoading}
      startIcon={isResolving ? <CircularProgress color="inherit" size={20} /> : undefined}
    />
  );

};

export const AddCertifiedUsersModal = (props: AddCertifiedUsersModalProps) => {

  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [step, setStep] = useState<ModalStep>('selectRecipients');
  const [recipients, setRecipients] = useState<DocumentAssignee[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validGroupIds, setValidGroupIds] = useState<Set<string> | null>(null);
  const [membersCountByGroupId, setMembersCountByGroupId] = useState<Map<string, number>>(new Map());
  const [appUserIdsByGroupId, setAppUserIdsByGroupId] = useState<Map<string, string[]>>(new Map());
  const [certifiedAssetIds, setCertifiedAssetIds] = useState<Set<string> | null>(null);

  const hasExternalChoices = props.choices !== undefined;

  const primaryCategory = props.categories[0];
  const requiresDocumentaryProof = props.categories.some((c) => c.isDocumentaryProofRequired);
  const isSingleDocumentFlow = requiresDocumentaryProof && recipients.length === 1;
  const isBulkDocumentFlow = requiresDocumentaryProof && recipients.length > 1;

  const isInternalExclusionLoading = !hasExternalChoices
    && Boolean(props.excludeExistingForCategoryId)
    && certifiedAssetIds === null;

  const { data: users = [] } = useGetList('app-users', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  }, { enabled: props.open && !hasExternalChoices });

  const { data: groups = [] } = useGetList('asset-groups', {
    pagination: { page: 1, perPage: -1 },
    sort: { field: 'name', order: 'ASC' },
    filter: {},
  }, { enabled: props.open && !hasExternalChoices });

  useEffect(() => {

    if (hasExternalChoices) {

      return undefined;

    }

    let cancelled = false;

    if (!props.open) {

      setAppUserIdsByGroupId(new Map());
      setValidGroupIds(null);
      setMembersCountByGroupId(new Map());

    } else if (groups.length === 0) {

      setAppUserIdsByGroupId(new Map());
      setValidGroupIds(new Set());
      setMembersCountByGroupId(new Map());

    } else {

      getGroupIdsWithAppUsers(groups).then(({
        validGroupIds: validIds,
        membersCountByGroupId: membersCountById,
        appUserIdsByGroupId: appUserIdsById,
      }) => {

        if (!cancelled) {

          setValidGroupIds(validIds);
          setMembersCountByGroupId(membersCountById);
          setAppUserIdsByGroupId(appUserIdsById);

        }

      });

    }

    return () => {

      cancelled = true;

    };

  }, [props.open, groups, hasExternalChoices]);

  useEffect(() => {

    if (hasExternalChoices) {

      return undefined;

    }

    let cancelled = false;

    if (!props.open || !props.excludeExistingForCategoryId) {

      setCertifiedAssetIds(null);
      return () => {

        cancelled = true;

      };

    }

    getCertifiedAssetIdsForCategory(props.excludeExistingForCategoryId).then((ids) => {

      if (!cancelled) setCertifiedAssetIds(ids);

    });

    return () => {

      cancelled = true;

    };

  }, [props.open, props.excludeExistingForCategoryId, hasExternalChoices]);

  const baseChoices = useMemo(() => {

    if (hasExternalChoices) return [];

    if (validGroupIds === null) {

      return buildAssigneeChoices(users, []);

    }

    return buildAssigneeChoices(
      users,
      groups.filter((group) => validGroupIds.has(String(group.id))),
      membersCountByGroupId,
    );

  }, [users, groups, validGroupIds, membersCountByGroupId, hasExternalChoices]);

  const internalChoices = useMemo(() => {

    if (hasExternalChoices) return [];
    if (isInternalExclusionLoading) return [];
    if (!certifiedAssetIds) return baseChoices;

    return filterAssigneeChoices(
      baseChoices,
      certifiedAssetIds,
      appUserIdsByGroupId,
    );

  }, [hasExternalChoices, baseChoices, certifiedAssetIds, appUserIdsByGroupId, isInternalExclusionLoading]);

  const defaultValues: CertifiedUsersFormValue = {
    recipientChoiceIds: [],
    certificationsByAssetId: {},
    document: undefined,
    documents: [],
    assignments: {},
  };

  const choices = hasExternalChoices ? props.choices : internalChoices;

  const resolvedCertifiedAssetIds = hasExternalChoices
    ? props.certifiedAssetIds ?? null
    : certifiedAssetIds;

  const isChoicesLoading = hasExternalChoices
    ? Boolean(props.isChoicesLoading)
    : isInternalExclusionLoading;

  useEffect(() => {

    if (!props.open) {

      setStep('selectRecipients');
      setRecipients([]);

    }

  }, [props.open]);

  const subtitleKey = {
    selectRecipients: 'resources.certification-categories.text.certified_users_select_subtitle',
    enterDates: 'resources.certification-categories.text.certified_users_dates_subtitle',
    uploadDocuments: '',
    assignDocuments: '',
  }[step];

  const categoryLabel = props.categories.map((c) => c.name).join(', ');

  const handleSubmit = async (values: FieldValues) => {

    setIsSubmitting(true);

    try {

      const certifiedUsersValues = values as CertifiedUsersFormValue;
      const hasRecipients = certifiedUsersValues.recipientChoiceIds.length > 0;

      let documentsByAssetId: Record<string, CertificationDocument> | undefined;
      if (hasRecipients && isBulkDocumentFlow) {

        const error = validateBulkAssignments(certifiedUsersValues.assignments, recipients);

        if (error) {

          notify(translate(error), { type: 'error' });
          return;

        }

        documentsByAssetId = buildDocumentsByAssetId(
          certifiedUsersValues.documents,
          certifiedUsersValues.assignments,
        );

      } else if (hasRecipients && isSingleDocumentFlow && certifiedUsersValues.document?.rawFile) {

        documentsByAssetId = { [recipients[0].id]: certifiedUsersValues.document };

      }

      let categories = props.categories;

      if (props.onCreateCategories) {

        categories = await props.onCreateCategories();

      }

      if (!hasRecipients) {

        notify(translate('resources.certification-categories.text.no_users_added'), { type: 'success' });
        refresh();
        props.onComplete?.();
        if (!props.onComplete) props.onClose();
        return;

      }

      await Promise.all(
        categories.flatMap((category) => {

          if (!category.id) return [];

          return recipients.map((recipient) => {

            const dates = certifiedUsersValues.certificationsByAssetId[recipient.id];

            if (!dates || !category.id) return Promise.resolve();
            return createUserCertification(
              dataProvider,
              recipient.id,
              category.id,
              dates,
              documentsByAssetId?.[recipient.id],
            );

          });

        }),
      );

      notify('resources.certification-categories.text.users_added', { type: 'success', messageArgs: { smart_count: recipients.length } });
      refresh();
      props.onComplete?.();
      if (!props.onComplete) props.onClose();

    } catch (error) {

      notify(
        error instanceof Error ? error.message : 'ra.notification.http_error',
        { type: 'error' },
      );

    } finally {

      setIsSubmitting(false);

    }

  };

  const handleBack = () => {

    if (step === 'enterDates') {

      setRecipients([]);
      setStep('selectRecipients');
      return;

    }
    if (step === 'uploadDocuments') setStep('enterDates');
    if (step === 'assignDocuments') setStep('uploadDocuments');

  };

  const showConfirm = (step === 'enterDates' && !isBulkDocumentFlow) || (step === 'assignDocuments');

  const showNext = step === 'selectRecipients'
    || (step === 'enterDates' && isBulkDocumentFlow)
    || step === 'uploadDocuments';

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="lg" fullWidth>
      <Form id="add-certified-users-form" defaultValues={defaultValues} onSubmit={handleSubmit} key={props.open ? 'open' : 'closed'}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" fontSize="20px">
            {translate('resources.certification-categories.text.certified_users_modal_title')}
          </Typography>
          <IconButton onClick={props.onClose} aria-label={translate('general.text.close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {translate(subtitleKey, { name: categoryLabel })}
            </Typography>
            {step === 'selectRecipients' && (
              <CertifiedUsersSelectionInput
                disabled={isChoicesLoading}
                label="resources.certification-categories.fields.certified_users"
                choices={choices ?? []}
              />
            )}
            {step === 'enterDates' && primaryCategory && (
              <>
                <CertifiedUsersDatesStep recipients={recipients} timeGranularity={primaryCategory.validityPeriod} />
                {isSingleDocumentFlow && (
                  <CertificationDocumentInput
                    source="document"
                    label={false}
                    validate={required()}
                    labelSingle={translate('resources.assets.certifications.text.drop_document_to_upload')}
                    accept={{ 'application/pdf': ['.pdf'], 'image/*': ['.jpg', '.jpeg', '.png'] }}
                  />
                )}
              </>
            )}
            {step === 'uploadDocuments' && (
              <>
                <Typography variant="body2" color="text.secondary">
                  {translate('resources.assets.certifications.text.bulk_upload_documents_helper', {
                    smart_count: recipients.length,
                  })}
                </Typography>
                <CertificationDocumentInput
                  source="documents"
                  multiple
                  label={false}
                  validate={[
                    required(),
                    validateBulkDocumentsUpload(recipients.length),
                  ]}
                  labelMultiple={translate('resources.assets.certifications.text.drop_documents_to_upload')}
                  accept={{ 'application/pdf': ['.pdf'], 'image/*': ['.jpg', '.jpeg', '.png'] }}
                />
              </>
            )}
            {step === 'assignDocuments' && (
              <BulkDocumentAssignmentStep recipients={recipients} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {step !== 'selectRecipients' && (
            <Button label="ra.action.back" onClick={handleBack} disabled={isSubmitting} />
          )}
          <Button label="ra.action.cancel" onClick={props.onClose} disabled={isSubmitting} />
          {showNext && (
            <CertifiedUsersNextButton
              isExclusionLoading={isChoicesLoading}
              step={step}
              choices={choices ?? []}
              recipients={recipients}
              isBulkDocumentFlow={isBulkDocumentFlow}
              validityPeriod={primaryCategory?.validityPeriod}
              certifiedAssetIds={resolvedCertifiedAssetIds}
              onRecipientsResolved={setRecipients}
              onNext={setStep}
              onSkip={() => handleSubmit({ ...defaultValues, recipientChoiceIds: [] })}
            />
          )}
          {showConfirm && (
            <Button
              type="submit"
              form="add-certified-users-form"
              label="ra.action.confirm"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress color="inherit" size={20} /> : undefined}
            />
          )}
        </DialogActions>
      </Form>
    </Dialog>
  );

};
